/**
 * Iter XX.12 — Cricket Journey Gate.
 *
 * The un-dismissable identity gate that appears once a player has
 * experienced real value (see /api/web/engagement-state). This is NOT a
 * paywall — the emotional frame is "we've built something worth keeping,
 * let's save it".
 *
 * Rules the founder set:
 *   · No X button.
 *   · No "continue anonymously" escape.
 *   · Copy explains WHY (memory / matches / videos / patterns / season),
 *     NOT "we need you to sign up".
 *   · Continue → opens the existing IdentitySheet in gated mode.
 */
import React from "react";

export default function CricketJourneyGate({ onContinue }) {
  return (
    <div
      className="fixed inset-0 z-40 bg-black/85 backdrop-blur-sm flex items-end sm:items-center justify-center px-3"
      data-testid="cricket-journey-gate"
      // No onClick — clicking the backdrop does nothing. Intentional.
      role="dialog"
      aria-modal="true"
      aria-labelledby="cj-gate-title"
    >
      <div
        className="relative w-full sm:max-w-md bg-gradient-to-b from-emerald-950/80 to-neutral-950 border border-emerald-900/40 rounded-t-3xl sm:rounded-3xl p-6 sm:p-8 shadow-2xl max-h-[100dvh] overflow-y-auto sm:max-h-[90dvh]"
        style={{
          // Iter XX.287 — Reserve <MobileBottomNav>'s measured height so
          // the primary "Continue" CTA is never occluded by the fixed
          // nav on iPhone Safari. Falls back to safe-area when nav is
          // not mounted (this gate is authenticated-user only, so nav
          // is always mounted in practice — fallback is defensive).
          paddingBottom: "calc(1.5rem + var(--gully-bottom-nav-h, env(safe-area-inset-bottom, 0px)))",
        }}
      >
        <div className="flex items-center justify-center mb-4">
          <div className="w-14 h-14 rounded-full bg-emerald-900/50 border border-emerald-700/40 flex items-center justify-center text-3xl"
               aria-hidden="true">
            🏏
          </div>
        </div>
        <h2
          id="cj-gate-title"
          className="text-lg sm:text-xl font-medium text-emerald-100 text-center mb-2"
          data-testid="cricket-journey-gate-title"
        >
          Before we keep going…
        </h2>
        <p className="text-sm text-neutral-300 leading-relaxed text-center mb-5">
          We&apos;ve started building something valuable together.
          I&apos;d hate for you to lose everything we&apos;ve talked about.
        </p>
        <div className="bg-neutral-900/50 border border-neutral-800/60 rounded-2xl p-4 mb-6 space-y-2">
          <p className="text-xs uppercase tracking-wider text-emerald-400/70 font-medium">
            Gully works best when it remembers your cricket
          </p>
          <ul className="text-sm text-neutral-300 space-y-1.5">
            <li className="flex gap-2"><span aria-hidden="true">·</span> I remember your matches</li>
            <li className="flex gap-2"><span aria-hidden="true">·</span> I connect your videos over time</li>
            <li className="flex gap-2"><span aria-hidden="true">·</span> I spot long-term patterns</li>
            <li className="flex gap-2"><span aria-hidden="true">·</span> I build your season</li>
            <li className="flex gap-2"><span aria-hidden="true">·</span> I help you improve over months</li>
          </ul>
        </div>
        <p className="text-sm text-neutral-300 text-center mb-5">
          Let&apos;s save your cricket journey.
        </p>
        <button
          onClick={onContinue}
          className="w-full py-3 rounded-2xl bg-emerald-500 hover:bg-emerald-400 text-neutral-950 font-medium transition-colors"
          data-testid="cricket-journey-gate-continue"
        >
          Continue →
        </button>
      </div>
    </div>
  );
}
